/**
 * Copyright (c) 2025 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import type { TypedDocumentNode } from '@apollo/client'
import { gql } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, PaginationControl, SearchExpand } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Spinner,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useRouterState } from '@tanstack/react-router'
import { SFProfileBanner } from '@web/src/components/primatives/sf-profile-banner/sf-profile-banner'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { EllipsisIcon, EyeOffIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { ProfileInfo, ProfileMediaInfo } from 'src/lib/fragments'
import type {
  FeedHiddenPageQuery,
  FeedHiddenPageQueryVariables,
  UnhideProfileFromFeedByIdMutation,
  UnhideProfileFromFeedByIdMutationVariables,
} from 'types/graphql'
import { z } from 'zod'

dayjs.extend(relativeTime)

type Translate = ReturnType<typeof useTranslation>['t']

function formatFeedHiddenDuration(expiresAt: string | null | undefined, t: Translate): string {
  if (!expiresAt) {
    return t('Permanent')
  }

  const expiry = dayjs(expiresAt)
  const relative = expiry.isBefore(dayjs()) ? t('EOD') : expiry.fromNow()
  return t('Until {{date}} ({{relative}})', {
    date: expiry.format('MMMM D, YYYY'),
    relative,
  })
}

const QUERY: TypedDocumentNode<FeedHiddenPageQuery, FeedHiddenPageQueryVariables> = gql`
  query FeedHiddenPage($page: Int, $filter: FeedHiddenPageFilter, $sortBy: FeedHiddenSortEnum) {
    feedHidden: feedHiddenPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        expiresAt
        hidden {
          ...ProfileInfo
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const UNHIDE_PROFILE_FROM_FEED: TypedDocumentNode<
  UnhideProfileFromFeedByIdMutation,
  UnhideProfileFromFeedByIdMutationVariables
> = gql`
  mutation UnhideProfileFromFeedById($id: String!) {
    unhideProfileFromFeedById(id: $id)
  }
`

type FeedHiddenListSort = 'OLDEST' | 'LATEST' | 'NAME'

type FeedHiddenSearchInput = {
  name?: string
  page?: number
  sortBy?: FeedHiddenListSort
}

function resolveFeedHiddenSearchParams(search: FeedHiddenSearchInput): {
  page: number
  name?: string
  sortBy: FeedHiddenListSort
} {
  return {
    page: search.page ?? 1,
    name: search.name,
    sortBy: search.sortBy ?? 'LATEST',
  }
}

export const Route = createFileRoute('/(auth)/(account-layout)/account/$profileId/feed-hidden')({
  component: PageComponent,
  validateSearch: z.object({
    name: z.string().optional(),
    page: z.coerce.number().optional(),
    sortBy: z.enum(['OLDEST', 'LATEST', 'NAME']).optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        name: '',
        sortBy: 'LATEST',
      }),
    ],
  },
  loaderDeps: ({ search }) => resolveFeedHiddenSearchParams(search),
  head: () =>
    seo({
      title: `Hidden From Feed | ${env.VITE_APP_NAME}`,
      description:
        'View and manage profiles hidden from your feed. Search by name and unhide profiles to see their posts again.',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params, deps }) => {
    const { queryRef } = await queryAndPreload(context, QUERY, {
      variables: {
        page: deps.page,
        sortBy: deps.sortBy,
        filter: {
          profileId: params.profileId,
          name: deps.name,
        },
      },
    })
    return { queryRef }
  },
})

function PageComponent() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<FeedHiddenPageQuery>)
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const search = Route.useSearch()
  const resolvedSearch = useMemo(() => resolveFeedHiddenSearchParams(search), [search])
  const { profileId } = Route.useParams()
  const [modalDialog, setModalDialog] = useState(false)
  const [unhideTarget, setUnhideTarget] = useState<{ id: string; name?: string | null } | null>(null)
  const navigate = Route.useNavigate()
  const { currentUser } = useAuth()

  const isLoading = useRouterState({ select: (s) => s.status === 'pending' })
  const [showLoading, setShowLoading] = useState(false)

  const formMethods = useForm({
    defaultValues: {
      name: search.name,
      sortBy: resolvedSearch.sortBy,
    },
  })

  useEffect(() => {
    if (!isLoading) {
      setShowLoading(false)
    }
  }, [isLoading])

  const [unhideProfileFromFeed] = useMutation(UNHIDE_PROFILE_FROM_FEED, {
    onError: (error) => {
      toast.error(error.message)
      setShowSpinnerValue(false)
    },
    update(cache, _, { variables }) {
      cache.evict({ id: cache.identify({ __typename: 'FeedHidden', id: variables?.id }) })
      cache.gc()
    },
  })

  const handleDialogAccept = () => {
    const target = unhideTarget
    if (!target?.id) {
      return
    }

    setShowSpinnerValue(true)
    setModalDialog(false)
    unhideProfileFromFeed({
      variables: { id: target.id },
      onCompleted: () => {
        toast.success(
          target.name
            ? t('{{name}} will appear in your feed again', { name: target.name })
            : t('Their posts will appear in your feed again')
        )
        setUnhideTarget(null)
        setShowSpinnerValue(false)
      },
    })
  }

  const handleFilter = ({ page, name, sortBy }: { page?: number; name?: string; sortBy?: string }): void => {
    setShowLoading(true)
    navigate({
      search: {
        page: page ?? resolvedSearch.page,
        sortBy: sortBy ?? resolvedSearch.sortBy,
        name: name ?? search.name,
      },
      replace: true,
    } as never)
  }

  const entryCount = data?.feedHidden?.entries?.length ?? 0

  return (
    <div className="mx-auto w-full max-w-[90rem] px-0">
      <div className="flex w-full flex-col gap-4">
        <h1 className="sr-only">{t('Hidden From Feed Listing')}</h1>

        <Form className="w-full" formMethods={formMethods} suppressHydrationWarning>
          <SearchExpand
            className="w-full"
            clearSearchLabel={t('Clear search')}
            fieldProps={{ className: 'basis-0 flex-1 min-w-0' }}
            loading={showLoading}
            name="name"
            onSearchChange={(value) => handleFilter({ name: value })}
            placeholder={t('Search hidden profiles...')}
            rightBarChildren={
              <div className="flex w-[min(100%,180px)] shrink-0 basis-[180px] items-start">
                <NativeSelectFormField
                  aria-label={t('Sort By')}
                  fieldProps={{ className: 'w-full max-w-full' }}
                  name="sortBy"
                  onValueChange={(e) => handleFilter({ sortBy: e.target.value })}
                  selectProps={{ className: 'w-full min-w-0' }}
                >
                  <option value="NAME">{t('Name')}</option>
                  <option value="LATEST">{t('Latest')}</option>
                  <option value="OLDEST">{t('Oldest')}</option>
                </NativeSelectFormField>
              </div>
            }
            toggleFiltersLabel={t('Toggle filters')}
          />
        </Form>

        {entryCount > 0 ? (
          <div className={cn('grid w-full grid-cols-1 gap-2')}>
            {data?.feedHidden?.entries.map((entry) => {
              return (
                <Card className="p-2" key={entry.id}>
                  <CardContent className="p-0">
                    <SFProfileBanner
                      lowerChild={formatFeedHiddenDuration(entry.expiresAt, t)}
                      profile={entry.hidden}
                      rightChild={
                        currentUser?.profile?.id === profileId && (
                          <DropdownMenu>
                            <DropdownMenuTrigger
                              render={
                                <Button
                                  aria-label={t('Menu')}
                                  className="rounded-full"
                                  disabled={showSpinner}
                                  size="icon-sm"
                                  type="button"
                                  variant="default"
                                >
                                  {showSpinner ? <Spinner className="size-4" /> : <EllipsisIcon className="size-4" />}
                                </Button>
                              }
                            />
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setUnhideTarget({ id: entry.id, name: entry.hidden?.name })
                                  setModalDialog(true)
                                }}
                              >
                                {t('Show in feed again')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )
                      }
                      shape="rounded"
                      size="2xl"
                    />
                  </CardContent>
                </Card>
              )
            })}
          </div>
        ) : (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <EyeOffIcon />
              </EmptyMedia>
              <EmptyTitle>
                {search.name
                  ? t('No hidden profiles found for "{{name}}"', { name: search.name })
                  : t('No profiles hidden from your feed at this time')}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        )}

        <PaginationControl
          count={data?.feedHidden?.count || 0}
          onPageChange={(page) => handleFilter({ page: page.page })}
          page={resolvedSearch.page}
          pageSize={data?.feedHidden?.perPage}
        />
      </div>

      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Show in feed')}
        loading={showSpinner}
        onConfirm={handleDialogAccept}
        onOpenChange={(e) => setModalDialog(e.open)}
        open={modalDialog}
        text={t('Their posts will appear in your feed again. This does not change blocking or messaging.')}
        title={
          unhideTarget?.name ? t('Show {{name}} in feed again?', { name: unhideTarget.name }) : t('Show in feed again?')
        }
        variant="basic"
      />
    </div>
  )
}
